<template>
  <div class="receipt-details-lookup">
    <p class="lead mb-1">How did you pay?</p>
    <p class="text-muted small">
      We need this to find the right card number for your receipt.
    </p>

    <!-- Step 1: physical card or mobile wallet -->
    <div class="row choice-row" role="radiogroup" aria-label="How did you pay?">
      <div class="col-6">
        <button
          type="button"
          role="radio"
          :aria-checked="paymentMethod === 'card' ? 'true' : 'false'"
          class="btn btn-lg btn-block choice-btn"
          :class="
            paymentMethod === 'card' ? 'btn-primary' : 'btn-outline-primary'
          "
          :disabled="searching"
          @click="selectPaymentMethod('card')"
        >
          <font-awesome-icon icon="credit-card" size="2x"></font-awesome-icon>
          <span class="choice-title">Card</span>
          <span class="choice-subtitle">Tapped, inserted or swiped</span>
        </button>
      </div>
      <div class="col-6">
        <button
          type="button"
          role="radio"
          :aria-checked="paymentMethod === 'wallet' ? 'true' : 'false'"
          class="btn btn-lg btn-block choice-btn"
          :class="
            paymentMethod === 'wallet' ? 'btn-primary' : 'btn-outline-primary'
          "
          :disabled="searching"
          @click="selectPaymentMethod('wallet')"
        >
          <font-awesome-icon icon="mobile-alt" size="2x"></font-awesome-icon>
          <span class="choice-title">Phone or watch</span>
          <span class="choice-subtitle"
            >Apple Pay, Google Pay, Samsung Pay&hellip;</span
          >
        </button>
      </div>
    </div>

    <!-- Physical card: diagram showing where the last four digits are -->
    <div v-if="paymentMethod === 'card'" class="method-panel text-left">
      <p class="mb-2">
        Enter the <strong>last 4 digits of the long number</strong> on your
        card.
      </p>
      <svg
        class="card-diagram"
        viewBox="0 0 340 262"
        role="img"
        aria-label="Diagram of a payment card with the last four digits of the long card number highlighted"
      >
        <defs>
          <linearGradient id="cardGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="#3d5a80" />
            <stop offset="100%" stop-color="#1d2d44" />
          </linearGradient>
        </defs>
        <rect
          x="2"
          y="2"
          width="336"
          height="212"
          rx="16"
          fill="url(#cardGradient)"
        />
        <!-- chip -->
        <rect
          x="30"
          y="62"
          width="46"
          height="36"
          rx="6"
          fill="#e9c46a"
          stroke="#c9a227"
        />
        <path
          d="M30 80 H76 M53 62 V98 M42 62 V72 M64 62 V72 M42 88 V98 M64 88 V98"
          stroke="#c9a227"
          stroke-width="1.5"
          fill="none"
        />
        <!-- contactless symbol -->
        <g fill="none" stroke="#ffffff" stroke-width="2.5" opacity="0.8">
          <path d="M92 70 a14 14 0 0 1 0 20" />
          <path d="M99 64 a22 22 0 0 1 0 32" />
          <path d="M106 58 a30 30 0 0 1 0 44" />
        </g>
        <!-- card number -->
        <text
          x="30"
          y="148"
          fill="#ffffff"
          font-family="Courier New, monospace"
          font-size="23"
          font-weight="bold"
          letter-spacing="1"
          opacity="0.75"
        >
          •••• •••• ••••
        </text>
        <rect
          x="238"
          y="122"
          width="80"
          height="36"
          rx="6"
          fill="#ffc107"
          fill-opacity="0.25"
          stroke="#ffc107"
          stroke-width="3"
        />
        <text
          x="278"
          y="148"
          fill="#ffffff"
          font-family="Courier New, monospace"
          font-size="23"
          font-weight="bold"
          text-anchor="middle"
        >
          1234
        </text>
        <!-- name and expiry -->
        <text
          x="30"
          y="192"
          fill="#ffffff"
          font-family="Helvetica, Arial, sans-serif"
          font-size="13"
          opacity="0.7"
        >
          MR A N OTHER
        </text>
        <text
          x="200"
          y="192"
          fill="#ffffff"
          font-family="Helvetica, Arial, sans-serif"
          font-size="13"
          opacity="0.7"
        >
          12/29
        </text>
        <!-- callout -->
        <path
          d="M278 238 V222 M270 230 L278 220 L286 230"
          stroke="#e0a800"
          stroke-width="3"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <text
          x="336"
          y="258"
          fill="#2c3e50"
          font-family="Helvetica, Arial, sans-serif"
          font-size="15"
          font-weight="bold"
          text-anchor="end"
        >
          Enter these 4 digits
        </text>
      </svg>
      <p class="small text-muted mt-2 mb-0">
        On some cards the long number is printed on the back instead of the
        front.
      </p>
    </div>

    <!-- Mobile wallet: pick the wallet, then show how to find the virtual number -->
    <div v-if="paymentMethod === 'wallet'" class="method-panel text-left">
      <div class="alert alert-info">
        <strong>Your phone or watch uses a different card number</strong> to the
        one printed on your card. You'll need the last 4 digits of that number
        to view your receipt &ndash; the digits on your physical card won't
        work.
      </div>

      <p class="mb-2"><strong>Which did you use?</strong></p>
      <div class="row wallet-row" role="radiogroup" aria-label="Mobile wallet">
        <div v-for="w in wallets" :key="w.id" class="col-6 col-sm-3">
          <button
            type="button"
            role="radio"
            :aria-checked="wallet === w.id ? 'true' : 'false'"
            class="btn btn-block wallet-btn"
            :class="wallet === w.id ? 'btn-primary' : 'btn-outline-secondary'"
            :disabled="searching"
            @click="selectWallet(w.id)"
          >
            <span class="wallet-icon">
              <svg
                v-if="w.logoPath"
                class="wallet-logo"
                :viewBox="w.logoViewBox"
                aria-hidden="true"
                focusable="false"
              >
                <path :d="w.logoPath" fill="currentColor" />
              </svg>
              <font-awesome-icon v-else :icon="w.icon" size="lg">
              </font-awesome-icon>
            </span>
            <span class="d-block">{{ w.name }}</span>
          </button>
        </div>
      </div>

      <div v-if="wallet" class="card bg-light p-3 mt-3 wallet-instructions">
        <div class="row">
          <div class="col-12 col-sm-8">
            <template v-if="wallet === 'apple'">
              <h5>Apple Pay on iPhone</h5>
              <ol>
                <li>Open the <strong>Wallet</strong> app.</li>
                <li>Tap the card you paid with.</li>
                <li>
                  Tap the <strong>More</strong> button
                  (&middot;&middot;&middot;) then <strong>Card Details</strong>.
                </li>
                <li>
                  Find the <strong>Device Account Number</strong> and enter its
                  last 4 digits below.
                </li>
              </ol>
              <h6>Paid with your Apple Watch?</h6>
              <p class="small">
                Your watch has its own number. On your iPhone, open the
                <strong>Watch</strong> app, tap
                <strong>Wallet &amp; Apple Pay</strong>, tap the card and look
                for the <strong>Device Account Number</strong>.
              </p>
              <p class="small mb-0">
                More help:
                <a
                  href="https://support.apple.com/en-gb/HT212779"
                  target="_blank"
                  rel="noopener"
                  >Apple support article</a
                >
              </p>
            </template>

            <template v-if="wallet === 'google'">
              <h5>Google Pay / Google Wallet</h5>
              <ol>
                <li>
                  Open the <strong>Google Wallet</strong> app (previously called
                  Google Pay).
                </li>
                <li>
                  Tap the card you paid with, then tap the
                  <strong>settings</strong> (cog) icon to show the card info.
                </li>
                <li>
                  Tap the <strong>More</strong> menu (&vellip;) to show more
                  card details.
                </li>
                <li>
                  Find the <strong>Virtual card number on this device</strong>
                  and enter its last 4 digits below.
                </li>
              </ol>
            </template>

            <template v-if="wallet === 'samsung'">
              <h5>Samsung Pay / Samsung Wallet</h5>
              <ol>
                <li>Open <strong>Samsung Wallet</strong> on your phone.</li>
                <li>
                  Tap the <strong>All</strong> tab, and then tap
                  <strong>Payment cards</strong>.
                </li>
                <li>Select the card you paid with.</li>
                <li>
                  The last 4 digits of the
                  <strong>digital card number</strong> will be displayed. Enter
                  them below.
                </li>
              </ol>
              <p class="small mb-0">
                More help:
                <a
                  href="https://www.samsung.com/us/support/answer/ANS10002610/"
                  target="_blank"
                  rel="noopener"
                  >Samsung support article</a
                >
              </p>
            </template>

            <template v-if="wallet === 'other'">
              <h5>Other phones, watches and wearables</h5>
              <p>
                This includes Fitbit Pay, Garmin Pay, smart rings, key fobs and
                banking apps that pay by phone.
              </p>
              <ol>
                <li>
                  Open the app you used to add your card to the device (for
                  example Fitbit, Garmin Connect or your bank's app).
                </li>
                <li>Find the card you paid with and open its details.</li>
                <li>
                  Look for a number called the
                  <strong>device account number</strong>,
                  <strong>virtual card number</strong> or
                  <strong>token number</strong> and enter its last 4 digits
                  below.
                </li>
              </ol>
              <p class="small mb-0">
                Can't find it? Your bank's app often lists the digital card
                numbers linked to your card, or your bank can tell you.
              </p>
            </template>
          </div>
          <div class="col-12 col-sm-4 text-center">
            <svg
              class="phone-diagram"
              viewBox="0 0 160 260"
              role="img"
              :aria-label="`Example card details screen with the ${currentWallet.numberLabel} highlighted`"
            >
              <rect
                x="4"
                y="4"
                width="152"
                height="252"
                rx="22"
                fill="#2c3e50"
              />
              <rect
                x="12"
                y="22"
                width="136"
                height="214"
                rx="8"
                fill="#ffffff"
              />
              <rect x="62" y="11" width="36" height="5" rx="2.5" fill="#555" />
              <!-- mini card -->
              <rect
                x="30"
                y="34"
                width="100"
                height="62"
                rx="7"
                fill="#3d5a80"
              />
              <rect
                x="40"
                y="50"
                width="16"
                height="12"
                rx="2"
                fill="#e9c46a"
              />
              <!-- detail rows -->
              <rect x="22" y="110" width="70" height="6" rx="3" fill="#ddd" />
              <rect x="22" y="122" width="100" height="6" rx="3" fill="#ddd" />
              <rect
                x="18"
                y="140"
                width="124"
                height="46"
                rx="6"
                fill="#fff3cd"
                stroke="#ffc107"
                stroke-width="2.5"
              />
              <text
                x="80"
                y="158"
                fill="#2c3e50"
                font-family="Helvetica, Arial, sans-serif"
                font-size="10"
                text-anchor="middle"
              >
                {{ currentWallet.numberLabel }}
              </text>
              <text
                x="80"
                y="177"
                fill="#2c3e50"
                font-family="Courier New, monospace"
                font-size="15"
                font-weight="bold"
                text-anchor="middle"
              >
                •••• 1234
              </text>
              <rect x="22" y="200" width="80" height="6" rx="3" fill="#ddd" />
              <rect x="22" y="212" width="110" height="6" rx="3" fill="#ddd" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- PAN last four entry, shown once we know which number to ask for -->
    <div v-if="showPanInput" class="form-group form-group-lg row mt-4">
      <label
        for="panLastFour"
        class="col-12 col-sm-5 offset-sm-1 col-form-label"
        >{{ panInputLabel }}:</label
      >
      <div class="col-8 col-sm-4">
        <input
          ref="panInput"
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          id="panLastFour"
          class="form-control form-control-lg"
          placeholder="- - - -"
          :aria-label="panInputLabel"
          maxlength="4"
          v-model="panLastFour"
          :disabled="searching"
        />
      </div>
      <div class="col-4 col-sm-2">
        <font-awesome-icon
          v-if="searching"
          icon="spinner"
          spin
          class="mt-2"
          size="lg"
        ></font-awesome-icon>
        <font-awesome-icon
          v-if="!searching"
          :icon="paymentMethod === 'wallet' ? 'mobile-alt' : 'credit-card'"
          class="mt-2"
          size="lg"
        ></font-awesome-icon>
      </div>
    </div>

    <div class="row" v-if="searching">
      <div class="col-12">
        <span>{{ searchProgressText }}</span>
        <div class="progress">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            :aria-valuenow="searchProgressPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
            :style="`width: ${searchProgressPercentage}%`"
          ></div>
        </div>
      </div>
    </div>

    <div v-if="error && !searching" class="alert alert-warning" role="alert">
      Error: {{ error }}
      <div v-if="paymentMethod !== 'wallet'" class="mt-2">
        <strong>Paid with your phone or watch?</strong>
        <a href="#" @click.prevent="selectPaymentMethod('wallet')"
          >Select &ldquo;Phone or watch&rdquo;</a
        >
        &ndash; your device uses a different card number.
      </div>
    </div>

    <button
      v-if="error && !searching && panLastFour.length === 4"
      class="btn btn-info btn-block"
      @click="searchReceipts"
    >
      Try again
      <font-awesome-icon icon="undo" class="ml-2"></font-awesome-icon>
    </button>
  </div>
</template>

<style scoped>
.choice-btn {
  height: 100%;
  white-space: normal;
  padding: 1rem 0.5rem;
}

.choice-title {
  display: block;
  font-weight: bold;
  margin-top: 0.5rem;
}

.choice-subtitle {
  display: block;
  font-size: 0.8rem;
  opacity: 0.85;
}

.choice-row > div,
.wallet-row > div {
  margin-bottom: 0.5rem;
}

.wallet-btn {
  height: 100%;
  white-space: normal;
  font-size: 0.9rem;
}

.wallet-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.75rem;
  margin-bottom: 0.25rem;
}

.wallet-logo {
  /* explicit size (viewBox is 24 x 3.8) as some browsers size width:auto SVGs at 300px */
  height: 0.85rem;
  width: 5.4rem;
  max-width: 100%;
}

.method-panel {
  margin-top: 1rem;
}

.card-diagram {
  display: block;
  width: 100%;
  max-width: 340px;
  margin: 0 auto;
}

.phone-diagram {
  width: 100%;
  max-width: 120px;
  margin-top: 0.5rem;
}

@media (min-width: 576px) {
  .phone-diagram {
    max-width: 160px;
  }
}

.wallet-instructions ol {
  padding-left: 1.25rem;
}
</style>

<script>
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: "ReceiptDetailsLookup",
  props: {
    receiptSlug: String,
    receiptId: String,
  },
  data() {
    return {
      panLastFour: "",
      // "card" (physical card) or "wallet" (Apple Pay, Google Pay etc.)
      paymentMethod: null,
      // which mobile wallet, when paymentMethod is "wallet"
      wallet: null,
      wallets: [
        {
          id: "apple",
          name: "Apple Pay",
          icon: ["fab", "apple"],
          numberLabel: "Device Account Number",
        },
        {
          id: "google",
          name: "Google Pay",
          icon: ["fab", "google"],
          numberLabel: "Virtual card number",
        },
        {
          id: "samsung",
          name: "Samsung Pay",
          // Font Awesome has no Samsung icon, so use the Samsung wordmark
          // from Simple Icons (https://simpleicons.org, CC0), cropped to fit
          logoViewBox: "0 10.1 24 3.8",
          logoPath:
            "M19.8166 10.2808l.0459 2.6934h-.023l-.7793-2.6934h-1.2837v3.3925h.8481l-.0458-2.785h.023l.8366 2.785h1.2264v-3.3925zm-16.149 0l-.6418 3.427h.9284l.4699-3.1175h.0229l.4585 3.1174h.9169l-.6304-3.4269zm5.1805 0l-.424 2.6132h-.023l-.424-2.6132H6.5788l-.0688 3.427h.8596l.023-3.0832h.0114l.573 3.0831h.8711l.5731-3.083h.023l.0228 3.083h.8596l-.0802-3.4269zm-7.2664 2.4527c.0343.0802.0229.1949.0114.2522-.0229.1146-.1031.2292-.3324.2292-.2177 0-.3438-.126-.3438-.3095v-.3323H0v.2636c0 .7679.6074.9971 1.2493.9971.6189 0 1.1346-.2178 1.2149-.7794.0458-.298.0114-.4928 0-.5616-.1605-.722-1.467-.9283-1.5588-1.3295-.0114-.0688-.0114-.1375 0-.1834.023-.1146.1032-.2292.3095-.2292.2063 0 .321.126.321.3095v.2063h.8595v-.2407c0-.745-.6762-.8596-1.1576-.8596-.6074 0-1.1117.2063-1.2034.7564-.023.149-.0344.2866.0114.4585.1376.7106 1.364.9169 1.5358 1.3524m11.152 0c.0343.0803.0228.1834.0114.2522-.023.1146-.1032.2292-.3324.2292-.2178 0-.3438-.126-.3438-.3095v-.3323h-.917v.2636c0 .7564.596.9857 1.2379.9857.6189 0 1.1232-.2063 1.2034-.7794.0459-.298.0115-.4814 0-.5616-.1375-.7106-1.4327-.9284-1.5243-1.318-.0115-.0688-.0115-.1376 0-.1835.0229-.1146.1031-.2292.3094-.2292.1948 0 .321.126.321.3095v.2063h.848v-.2407c0-.745-.6647-.8596-1.146-.8596-.6075 0-1.1004.1948-1.192.7564-.023.149-.023.2866.0114.4585.1376.7106 1.341.9054 1.513 1.3524m2.8882.4585c.2407 0 .3094-.1605.3323-.2522.0115-.0343.0115-.0917.0115-.126v-2.533h.871v2.4642c0 .0688 0 .1948-.0114.2292-.0573.6419-.5616.8482-1.192.8482-.6303 0-1.1346-.2063-1.192-.8482 0-.0344-.0114-.1604-.0114-.2292v-2.4642h.871v2.533c0 .0458 0 .0916.0115.126 0 .0917.0688.2522.3095.2522m7.1518-.0344c.2522 0 .3324-.1605.3553-.2522.0115-.0343.0115-.0917.0115-.126v-.4929h-.3553v-.5043H24v.917c0 .0687 0 .1145-.0115.2292-.0573.6303-.596.8481-1.2034.8481-.6075 0-1.1461-.2178-1.2034-.8481-.0115-.1147-.0115-.1605-.0115-.2293v-1.444c0-.0574.0115-.172.0115-.2293.0802-.6419.596-.8482 1.2034-.8482s1.1347.2063 1.2034.8482c.0115.1031.0115.2292.0115.2292v.1146h-.8596v-.1948s0-.0803-.0115-.1261c-.0114-.0802-.0802-.2521-.3438-.2521-.2521 0-.321.1604-.3438.2521-.0115.0458-.0115.1032-.0115.1605v1.5702c0 .0458 0 .0916.0115.126 0 .0917.0917.2522.3323.2522",
          numberLabel: "Digital card number",
        },
        {
          id: "other",
          name: "Other",
          icon: "wallet",
          numberLabel: "Device / virtual card number",
        },
      ],
      error: null,
      searching: false,
      searchProgressPercentage: 0,
      searchProgressText: "",
    };
  },
  computed: {
    currentWallet() {
      return this.wallets.find((w) => w.id === this.wallet) || {};
    },
    showPanInput() {
      return (
        this.paymentMethod === "card" ||
        (this.paymentMethod === "wallet" && !!this.wallet)
      );
    },
    panInputLabel() {
      if (this.paymentMethod === "wallet") {
        let label = this.currentWallet.numberLabel || "virtual card number";
        // Apple's "Device Account Number" is a proper name; others read as prose
        if (this.wallet !== "apple") {
          label = label.charAt(0).toLowerCase() + label.slice(1);
        }
        return `Last 4 digits of the ${label}`;
      }
      return "Last 4 digits of your card number";
    },
  },
  watch: {
    panLastFour: function (val) {
      if (val && val.length === 4) {
        if (this.receiptSlug) {
          this.searchReceipts();
        } else if (this.receiptId) {
          this.navigateToReceipt(this.receiptId, val);
        }
      }
    },
  },
  methods: {
    selectPaymentMethod(method) {
      if (this.paymentMethod !== method) {
        this.paymentMethod = method;
        this.wallet = null;
        this.resetPanEntry();
      }
      if (method === "card") {
        this.focusPanInput();
      }
    },
    selectWallet(wallet) {
      if (this.wallet !== wallet) {
        this.wallet = wallet;
        this.resetPanEntry();
      }
      // don't auto-focus the input here: on mobile the keyboard would cover
      // the instructions the customer needs to read first
    },
    resetPanEntry() {
      this.panLastFour = "";
      this.error = null;
    },
    focusPanInput() {
      this.$nextTick(() => {
        if (this.$refs.panInput) {
          this.$refs.panInput.focus();
        }
      });
    },
    async searchReceipts() {
      this.error = null;

      // handle issues covered by trailing slash in base URL (causing slug to start with leading slash)
      let slug = this.receiptSlug;
      if (slug.startsWith("/")) {
        slug = slug.split("/")[1];
      }

      this.searching = true;

      const attempts = [
        {
          expectedCompletionSeconds: 10,
          afterFailureWaitSeconds: 1,
          progressText: "Looking up transaction.",
        },
        {
          expectedCompletionSeconds: 11,
          afterFailureWaitSeconds: 2,
          progressText: "Getting things ready...",
        },
        {
          expectedCompletionSeconds: 12,
          afterFailureWaitSeconds: 3,
          progressText: "Generating your receipt.",
        },
        {
          expectedCompletionSeconds: 13,
          afterFailureWaitSeconds: 4,
          progressText: "This won't take long.",
        },
        {
          expectedCompletionSeconds: 14,
          afterFailureWaitSeconds: 5,
          progressText: "Still working on it!",
        },
        {
          expectedCompletionSeconds: 15,
          afterFailureWaitSeconds: 6,
          progressText: "This might take a minute.",
        },
        {
          expectedCompletionSeconds: 16,
          afterFailureWaitSeconds: 7,
          progressText: "Sorry for the delay.",
        },
        {
          expectedCompletionSeconds: 17,
          afterFailureWaitSeconds: 8,
          progressText: "This is taking longer than usual.",
        },
        {
          expectedCompletionSeconds: 18,
          afterFailureWaitSeconds: 9,
          progressText: "It should be along in a jiffy.",
        },
        {
          expectedCompletionSeconds: 19,
          afterFailureWaitSeconds: 10,
          progressText: "Just need a little more time.",
        },
        {
          expectedCompletionSeconds: 20,
          afterFailureWaitSeconds: 11,
          progressText: "Hmm, something seems to be wrong",
        },
        {
          expectedCompletionSeconds: 21,
          afterFailureWaitSeconds: 12,
          progressText: "Still trying for you...",
        },
        {
          expectedCompletionSeconds: 22,
          afterFailureWaitSeconds: 13,
          progressText: "It doesn't usually take this long",
        },
        {
          expectedCompletionSeconds: 23,
          afterFailureWaitSeconds: 14,
          progressText: "The cogs are whirring...",
        },
        {
          expectedCompletionSeconds: 24,
          afterFailureWaitSeconds: 15,
          progressText: "We might need to try again.",
        },
        {
          expectedCompletionSeconds: 25,
          afterFailureWaitSeconds: 20,
          progressText: "Trying one more time...",
        },
      ];

      let elapsedTime = 0;
      console.log("Starting timer");

      // loop for progress indicator
      let expectedCompletionMs = attempts[0].expectedCompletionSeconds * 1000;
      const intervalMs = 500;
      const interval = setInterval(() => {
        elapsedTime += intervalMs;

        // calculate progress
        this.searchProgressPercentage = Math.round(
          (elapsedTime / expectedCompletionMs) * 100
        );
        console.log(
          `${elapsedTime}ms elapsed of expected ${expectedCompletionMs}ms (${this.searchProgressPercentage}%)`
        );
      }, intervalMs);

      for (const attempt of attempts) {
        expectedCompletionMs = attempt.expectedCompletionSeconds * 1000;

        this.searchProgressText = attempt.progressText;

        // make the request
        try {
          let response;
          response = await this.api.get("/v1/receipts", {
            params: {
              slug,
              panLastFour: this.panLastFour,
              now: new Date().toISOString(),
            },
          });

          if (response.data && response.data.length > 0) {
            // clear interval, set progress to 100
            this.searchProgressPercentage = 100;
            clearInterval(interval);

            this.searching = false;

            // a receipt has been found
            const receipt = response.data[0];
            this.navigateToReceipt(receipt.id, this.panLastFour);
            return null;
          } else {
            // wait a bit
            console.log(
              `waiting ${attempt.afterFailureWaitSeconds} seconds before trying again`
            );

            const delaySeconds =
              attempt.expectedCompletionSeconds +
              attempt.afterFailureWaitSeconds;
            const delayMs = delaySeconds * 1000;
            expectedCompletionMs = elapsedTime + delayMs;
            await sleep(attempt.afterFailureWaitSeconds * 1000);
          }
        } catch (error) {
          // clear interval, set progress to 0
          this.searchProgressPercentage = 0;
          clearInterval(interval);
          this.searching = false;
          if (error.response && error.response.data && error.response.data) {
            this.error = error.response.data.message;
          } else if (error.message) {
            this.error = error.message;
          } else {
            this.error = "An unknown error occurred.";
          }
        }
      }

      // all attempts done, no result
      if (!this.error) {
        // still no joy
        // clear interval, set progress to 0
        this.searchProgressPercentage = 0;
        clearInterval(interval);
        this.searching = false;
        this.error =
          "Sorry, we couldn't find your receipt. If the transaction has recently been processed, we might need a bit more time to generate it. Please try again in a few moments.";
      }
    },
    navigateToReceipt(receiptId, panLastFour) {
      this.$router.push({
        path: `/receipts/${receiptId}`,
        query: { panLastFour: panLastFour },
      });
    },
  },
  created() {
    // check the query param
    if (this.$route.query && this.$route.query.panLastFour) {
      this.panLastFour = this.$route.query.panLastFour;
    }
  },
};
</script>
